'use client';

import { type Serwist } from '@serwist/window';
import { useEffect } from 'react';

import { PWAInstallBanner } from './pwainstall-banner';

declare global {
  interface Window {
    serwist: Serwist;
  }
}

/**
 * Checks if the browser has a network connection and if the service worker script can be fetched.
 */
async function checkForNetworkConnection() {
  const onLine = navigator && 'connection' in navigator ? navigator.onLine : true;
  if (!onLine) {
    console.warn('No network connection available. Service Worker will not update.');
    return false;
  }

  try {
    const response = await fetch('/sw.js', {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    return response.status === 200;
  } catch (e) {
    console.error('Failed to fetch service worker script:', e);
    return false;
  }
}

export default function RegisterPWA() {
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined = undefined;

    // Check for initial network connection and register service worker
    const register = async () => {
      if ('serviceWorker' in navigator && window.serwist !== undefined) {
        const isOnline = await checkForNetworkConnection();
        if (!isOnline) {
          console.warn('Waiting for network connection to register service worker.');
          return;
        }

        clearInterval(interval);
        await window.serwist.register();
        await window.serwist.update();
      } else {
        // If there is no service worker support, there is not point in continuing
        // to check for network connection.
        clearInterval(interval);
        console.warn('Service Worker API or Serwist is not available in this browser.');
      }
    };

    interval = setInterval(register, 1000);
    return () => clearInterval(interval);
  }, []);

  return <PWAInstallBanner />;
}
